import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GoogleSignInButton } from '../../components/auth/GoogleSignInButton';
import { AuthField } from '../../components/auth/AuthField';
import { AuthPageHeader } from '../../components/auth/AuthPageHeader';
import { AuthPageLayout } from '../../components/auth/AuthPageLayout';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { useAuth } from '../../contexts/AuthContext';
import { fetchAuthMethods, TenantAuthMethods } from '../../services/authApi';
import { getStoredAccessToken } from '../../services/apiClient';

const devTenantSlug = process.env.NODE_ENV === 'development'
  ? (process.env.REACT_APP_DEV_TENANT_SLUG?.trim() || 'admin')
  : process.env.REACT_APP_DEV_TENANT_SLUG?.trim();

const DEFAULT_AUTH_METHODS: TenantAuthMethods = {
  allowEmailPassword: true,
  allowGoogle: false,
};

export function LoginPage() {
  const { login, loginWithGoogle, isLoading, profile } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [authMethods, setAuthMethods] = useState<TenantAuthMethods>(DEFAULT_AUTH_METHODS);
  const [loadingMethods, setLoadingMethods] = useState(true);

  useEffect(() => {
    void (async () => {
      setLoadingMethods(true);
      try {
        const methods = await fetchAuthMethods(devTenantSlug);
        setAuthMethods(methods);
      } catch {
        setAuthMethods(DEFAULT_AUTH_METHODS);
      } finally {
        setLoadingMethods(false);
      }
    })();
  }, []);

  const handleGoogleCredential = useCallback(async (idToken: string) => {
    if (!devTenantSlug) {
      toast.error('Organização não configurada para login com Google.');
      return;
    }

    setSubmitting(true);
    try {
      await loginWithGoogle(idToken, devTenantSlug);
    } catch {
      toast.error('Não foi possível entrar com Google.');
    } finally {
      setSubmitting(false);
    }
  }, [loginWithGoogle]);

  if (!isLoading && getStoredAccessToken() && profile) {
    return <Navigate to={profile.portalOnly ? '/portal' : '/dashboard'} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    try {
      await login({
        email: email.trim(),
        password,
        tenantSlug: devTenantSlug || undefined,
      });
    } catch {
      toast.error('Não foi possível entrar. Verifique e-mail e senha.');
    } finally {
      setSubmitting(false);
    }
  }

  const showEmailLogin = authMethods.allowEmailPassword;
  const showGoogleLogin =
    authMethods.allowGoogle
    && !!authMethods.googleClientId
    && !!authMethods.googleAllowedDomain;
  const showDivider = showEmailLogin && showGoogleLogin;

  return (
    <AuthPageLayout>
      <AuthPageHeader subtitle="Acesse com sua conta corporativa" />

      {loadingMethods ? (
        <p className="text-center text-sm text-secondary-500">Carregando opções de login...</p>
      ) : null}

      {!loadingMethods && showGoogleLogin && authMethods.googleClientId ? (
        <div className="space-y-4">
          {authMethods.googleAllowedDomain ? (
            <p className="text-center text-xs text-secondary-500">
              Use sua conta Google Workspace @{authMethods.googleAllowedDomain}
            </p>
          ) : null}
          <GoogleSignInButton
            clientId={authMethods.googleClientId}
            hostedDomain={authMethods.googleAllowedDomain ?? undefined}
            onCredential={(idToken) => void handleGoogleCredential(idToken)}
            disabled={submitting}
          />
        </div>
      ) : null}

      {showDivider ? (
        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-secondary-200" />
          <span className="text-xs font-medium uppercase text-secondary-400">ou</span>
          <div className="h-px flex-1 bg-secondary-200" />
        </div>
      ) : null}

      {!loadingMethods && showEmailLogin ? (
        <form className="auth-form" onSubmit={(e) => void handleSubmit(e)}>
          <AuthField label="E-mail" htmlFor="email">
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              className="auth-field-input"
            />
          </AuthField>

          <AuthField label="Senha" htmlFor="password">
            <PasswordInput
              id="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Digite sua senha"
              className="auth-field-input"
            />
          </AuthField>

          <button
            type="submit"
            disabled={submitting}
            className="erp-btn-primary w-full py-2.5"
          >
            {submitting ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      ) : null}

      {!loadingMethods && !showEmailLogin && !showGoogleLogin ? (
        <p className="text-center text-sm text-secondary-600">
          Nenhum método de login está disponível. Contate o administrador da empresa.
        </p>
      ) : null}

      {!loadingMethods && showEmailLogin ? (
        <p className="mt-6 text-center text-sm text-secondary-500">
          <Link to="/forgot-password" className="font-medium text-primary-600 hover:text-primary-700">
            Esqueci minha senha
          </Link>
        </p>
      ) : null}
    </AuthPageLayout>
  );
}
